import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { format } from 'date-fns';

function Payments() {
  const { vin } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const [vehicle, setVehicle] = useState(null);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newPayment, setNewPayment] = useState({
    amount: '',
    date_paid: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'cash',
    notes: ''
  });

  const [editPayment, setEditPayment] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [showEditPlanDialog, setShowEditPlanDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const [editPlanData, setEditPlanData] = useState({
    customer_name: '',
    amount_financed: '',
    down_payment: '',
    interest_rate: '',
    term_months: '',
  });

  const [newPlanData, setNewPlanData] = useState({
    customer_name: '',
    amount_financed: '0',
    down_payment: '0',
    interest_rate: '0',
    term_months: '',
  });

  const [fieldErrorsEdit, setFieldErrorsEdit] = useState({});

  useEffect(() => {
    fetchVehicleDetails();
    fetchPaymentPlan();
  }, [vin]);

  const fetchVehicleDetails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/${vin}`);
      const data = await response.json();
      if (response.ok) {
        setVehicle(data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to fetch vehicle details');
    }
  };

  const fetchPaymentPlan = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${vin}?username=${username}`);
      if (response.status === 404) {
        setPaymentPlan(null);
        setLoading(false);
        return;
      }
      const data = await response.json();
      if (response.ok) {
        setPaymentPlan(data);
        setEditPlanData({
          customer_name: data.customer_name,
          amount_financed: data.amount_financed,
          down_payment: data.down_payment,
          interest_rate: data.interest_rate,
          term_months: data.term_months,
        });
      }
    } catch (err) {
      console.error('Error fetching payment plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async () => {
    try {
      setFieldErrors({});
      const errors = {};
      if (!newPlanData.customer_name || newPlanData.customer_name.trim() === '') {
        errors.customer_name = 'Customer name is required';
      }
      const amountFinanced = parseFloat(newPlanData.amount_financed);
      if (!newPlanData.amount_financed || newPlanData.amount_financed.trim() === '' || isNaN(amountFinanced) || amountFinanced <= 0) {
        errors.amount_financed = 'Amount financed must be greater than 0';
      }
      const termMonths = parseInt(newPlanData.term_months);
      if (!newPlanData.term_months || newPlanData.term_months.trim() === '' || isNaN(termMonths) || termMonths <= 0) {
        errors.term_months = 'Term months must be greater than 0';
      }
      const downPayment = parseFloat(newPlanData.down_payment);
      if (newPlanData.down_payment === '' || newPlanData.down_payment.trim() === '' || isNaN(downPayment) || downPayment < 0) {
        errors.down_payment = 'Down payment must be 0 or greater';
      }
      const interestRate = parseFloat(newPlanData.interest_rate);
      if (newPlanData.interest_rate === '' || newPlanData.interest_rate.trim() === '' || isNaN(interestRate) || interestRate < 0) {
        errors.interest_rate = 'Interest rate must be 0 or greater';
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Please fix the errors above');
        return;
      }
      const username = localStorage.getItem('username');
      const requestData = {
        ...newPlanData,
        username,
        vin,
        vehicle_make: vehicle?.make,
        vehicle_model: vehicle?.model,
      };
      
      console.log('DEBUG - Sending payment plan data:', requestData);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      console.log('DEBUG - Response status:', response.status);
      
      if (response.ok) {
        fetchPaymentPlan();
        setNewPlanData({
          customer_name: '',
          amount_financed: '0',
          down_payment: '0',
          interest_rate: '0',
          term_months: '',
        });
        setError(null); // Clear any previous errors
        setFieldErrors({}); // Clear field errors
      } else {
        const data = await response.json();
        console.log('DEBUG - Error response:', data);
        setError(data.error);
      }
    } catch (err) {
      console.log('DEBUG - Network error:', err);
      setError('Failed to create payment plan');
    }
  };

  const handleUpdatePlan = async () => {
    try {
      setFieldErrorsEdit({});
      const errors = {};
      if (!editPlanData.customer_name || editPlanData.customer_name.trim() === '') {
        errors.customer_name = 'Customer name is required';
      }
      const amountFinanced = parseFloat(editPlanData.amount_financed);
      if (!editPlanData.amount_financed || editPlanData.amount_financed.trim() === '' || isNaN(amountFinanced) || amountFinanced <= 0) {
        errors.amount_financed = 'Amount financed must be greater than 0';
      }
      const termMonths = parseInt(editPlanData.term_months);
      if (!editPlanData.term_months || editPlanData.term_months.trim() === '' || isNaN(termMonths) || termMonths <= 0) {
        errors.term_months = 'Term months must be greater than 0';
      }
      const downPayment = parseFloat(editPlanData.down_payment);
      if (editPlanData.down_payment === '' || editPlanData.down_payment.trim() === '' || isNaN(downPayment) || downPayment < 0) {
        errors.down_payment = 'Down payment must be 0 or greater';
      }
      const interestRate = parseFloat(editPlanData.interest_rate);
      if (editPlanData.interest_rate === '' || editPlanData.interest_rate.trim() === '' || isNaN(interestRate) || interestRate < 0) {
        errors.interest_rate = 'Interest rate must be 0 or greater';
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrorsEdit(errors);
        setError('Please fix the errors above');
        return;
      }
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${vin}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editPlanData,
          username,
        }),
      });
      if (response.ok) {
        fetchPaymentPlan();
        setShowEditPlanDialog(false);
        setError(null);
        setFieldErrorsEdit({});
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to update payment plan');
    }
  };

  const handleAddPayment = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${vin}/payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...newPayment,
          username,
        }),
      });

      if (response.ok) {
        fetchPaymentPlan();
        setNewPayment({
          amount: '',
          date_paid: format(new Date(), 'yyyy-MM-dd'),
          payment_method: 'cash',
          notes: ''
        });
        setShowPaymentDialog(false);
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to add payment');
    }
  };

  const handleUpdatePayment = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${vin}/payment/${editPayment.payment_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editPayment,
          username,
        }),
      });

      if (response.ok) {
        fetchPaymentPlan();
        setEditPayment(null);
        setShowPaymentDialog(false);
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to update payment');
    }
  };

  const handleDeletePayment = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${vin}/payment/${selectedPaymentId}?username=${username}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchPaymentPlan();
        setShowDeleteDialog(false);
        setSelectedPaymentId(null);
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to delete payment');
    }
  };

  const handleCompletePlan = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${vin}/complete?username=${username}`, {
        method: 'PUT',
      });
      
      if (response.ok) {
        fetchPaymentPlan();
        setShowCompleteDialog(false);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to complete payment plan');
      }
    } catch (err) {
      setError('Error completing payment plan');
    }
  };

  const calculateRemainingBalance = () => {
    if (!paymentPlan) return 0;
    const totalPaid = paymentPlan.payments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
    return paymentPlan.amount_financed - totalPaid;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Left Panel - Payment Plan Details */}
        <Box sx={{ 
          width: { xs: '100%', md: '400px' },
          flexShrink: 0
        }}>
          <Paper sx={{ 
            p: 3,
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.9)
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              variant="outlined"
              sx={{ mb: 3 }}
            >
              Back
            </Button>

            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
              Payment Plan Details
            </Typography>

            {vehicle && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  VIN: {vin}
                </Typography>
                {vehicle.sale_status === 'sold' && vehicle.sale_price && (
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold', mt: 0.5 }}>
                    Sold for: {formatCurrency(vehicle.sale_price)}
                  </Typography>
                )}
              </Box>
            )}

            {!loading && !paymentPlan && (
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ color: theme.palette.primary.main }}>
                  Create Payment Plan
                </Typography>
                <form onSubmit={e => { e.preventDefault(); handleCreatePlan(); }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Name *"
                        required
                        value={newPlanData.customer_name}
                        onChange={(e) => setNewPlanData({ ...newPlanData, customer_name: e.target.value })}
                        error={!!fieldErrors.customer_name}
                        helperText={fieldErrors.customer_name}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Amount Financed *"
                        type="number"
                        required
                        value={newPlanData.amount_financed}
                        onChange={(e) => setNewPlanData({ ...newPlanData, amount_financed: e.target.value })}
                        error={!!fieldErrors.amount_financed}
                        helperText={fieldErrors.amount_financed}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Down Payment *"
                        type="number"
                        required
                        value={newPlanData.down_payment}
                        onChange={(e) => setNewPlanData({ ...newPlanData, down_payment: e.target.value })}
                        error={!!fieldErrors.down_payment}
                        helperText={fieldErrors.down_payment}
                        InputLabelProps={{
                          required: false
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate (%) *"
                        type="number"
                        required
                        value={newPlanData.interest_rate}
                        onChange={(e) => setNewPlanData({ ...newPlanData, interest_rate: e.target.value })}
                        error={!!fieldErrors.interest_rate}
                        helperText={fieldErrors.interest_rate}
                        InputLabelProps={{
                          required: false
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Term (Months) *"
                        type="number"
                        required
                        value={newPlanData.term_months}
                        onChange={(e) => setNewPlanData({ ...newPlanData, term_months: e.target.value })}
                        error={!!fieldErrors.term_months}
                        helperText={fieldErrors.term_months}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        sx={{
                          mt: 2,
                          background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                          boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                        }}
                      >
                        Create Payment Plan
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            )}

            {paymentPlan && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main }}>
                    Plan Summary
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setShowEditPlanDialog(true)}
                      size="small"
                    >
                      Edit
                    </Button>
                    {paymentPlan.status === 'active' && (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => setShowCompleteDialog(true)}
                        size="small"
                      >
                        Mark Complete
                      </Button>
                    )}
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Amount Financed</Typography>
                    <Typography variant="h6">{formatCurrency(paymentPlan.amount_financed)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Down Payment</Typography>
                    <Typography variant="h6">{formatCurrency(paymentPlan.down_payment)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h6">{formatCurrency(paymentPlan.total_amount)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Monthly Payment</Typography>
                    <Typography variant="h6">{formatCurrency(paymentPlan.monthly_payment)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Remaining Balance</Typography>
                    <Typography variant="h6" color="error">{formatCurrency(calculateRemainingBalance())}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Interest Rate</Typography>
                    <Typography variant="h6">{paymentPlan.interest_rate}%</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Term</Typography>
                    <Typography variant="h6">{paymentPlan.term_months} months</Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Right Panel - Payment History */}
        <Box sx={{ flexGrow: 1 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ color: theme.palette.primary.main }}>Payment History</Typography>
              {paymentPlan && (
                <Button
                  variant="contained"
                  onClick={() => setShowPaymentDialog(true)}
                  sx={{
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                  }}
                >
                  Add Payment
                </Button>
              )}
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date Paid</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Payment Method</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentPlan && paymentPlan.payments && paymentPlan.payments.length > 0 ? (
                    paymentPlan.payments.map((payment) => (
                      <TableRow key={payment.payment_id} hover>
                        <TableCell>{format(new Date(payment.date_paid), 'MM/dd/yyyy')}</TableCell>
                        <TableCell>{formatCurrency(payment.amount)}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{payment.payment_method}</TableCell>
                        <TableCell>{payment.notes}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => {
                              setEditPayment(payment);
                              setShowPaymentDialog(true);
                            }}
                            size="small"
                            sx={{ mr: 1 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => {
                              setSelectedPaymentId(payment.payment_id);
                              setShowDeleteDialog(true);
                            }}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {!paymentPlan ? 'Create a payment plan to start tracking payments' : 'No payments recorded yet'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>

      {/* Add/Edit Payment Dialog */}
      <Dialog open={showPaymentDialog} onClose={() => {
        setShowPaymentDialog(false);
        setEditPayment(null);
      }}>
        <DialogTitle>{editPayment ? 'Edit Payment' : 'Add Payment'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={editPayment ? editPayment.amount : newPayment.amount}
                  onChange={(e) => editPayment 
                    ? setEditPayment({ ...editPayment, amount: e.target.value })
                    : setNewPayment({ ...newPayment, amount: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date Paid"
                  type="date"
                  value={editPayment ? editPayment.date_paid : newPayment.date_paid}
                  onChange={(e) => editPayment 
                    ? setEditPayment({ ...editPayment, date_paid: e.target.value })
                    : setNewPayment({ ...newPayment, date_paid: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={editPayment ? editPayment.payment_method : newPayment.payment_method}
                    onChange={(e) => editPayment 
                      ? setEditPayment({ ...editPayment, payment_method: e.target.value })
                      : setNewPayment({ ...newPayment, payment_method: e.target.value })
                    }
                    label="Payment Method"
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={editPayment ? editPayment.notes : newPayment.notes}
                  onChange={(e) => editPayment 
                    ? setEditPayment({ ...editPayment, notes: e.target.value })
                    : setNewPayment({ ...newPayment, notes: e.target.value })
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowPaymentDialog(false);
            setEditPayment(null);
          }}>
            Cancel
          </Button>
          <Button 
            onClick={editPayment ? handleUpdatePayment : handleAddPayment}
            variant="contained"
          >
            {editPayment ? 'Update' : 'Add'} Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Payment Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Payment</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this payment?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeletePayment} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Plan Dialog */}
      <Dialog open={showEditPlanDialog} onClose={() => setShowEditPlanDialog(false)}>
        <DialogTitle>Edit Payment Plan</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  required
                  value={editPlanData.customer_name}
                  onChange={(e) => setEditPlanData({ ...editPlanData, customer_name: e.target.value })}
                  error={!!fieldErrorsEdit.customer_name}
                  helperText={fieldErrorsEdit.customer_name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount Financed"
                  type="number"
                  required
                  value={editPlanData.amount_financed}
                  onChange={(e) => setEditPlanData({ ...editPlanData, amount_financed: e.target.value })}
                  error={!!fieldErrorsEdit.amount_financed}
                  helperText={fieldErrorsEdit.amount_financed}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Down Payment (Optional)"
                  type="number"
                  placeholder="0"
                  value={editPlanData.down_payment}
                  onChange={(e) => setEditPlanData({ ...editPlanData, down_payment: e.target.value })}
                  helperText={fieldErrorsEdit.down_payment || 'Leave empty for $0 down payment'}
                  error={!!fieldErrorsEdit.down_payment}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Interest Rate (%) - Optional"
                  type="number"
                  placeholder="0"
                  value={editPlanData.interest_rate}
                  onChange={(e) => setEditPlanData({ ...editPlanData, interest_rate: e.target.value })}
                  helperText={fieldErrorsEdit.interest_rate || 'Leave empty for 0% interest'}
                  error={!!fieldErrorsEdit.interest_rate}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Term (Months)"
                  type="number"
                  required
                  value={editPlanData.term_months}
                  onChange={(e) => setEditPlanData({ ...editPlanData, term_months: e.target.value })}
                  error={!!fieldErrorsEdit.term_months}
                  helperText={fieldErrorsEdit.term_months}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditPlanDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdatePlan} variant="contained">
            Update Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Payment Plan Dialog */}
      <Dialog
        open={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Complete Payment Plan</Typography>
        </DialogTitle>
        <DialogContent>
          {paymentPlan && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to mark this payment plan as completed?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>Payment Plan Details:</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Vehicle: {vehicle?.make} {vehicle?.model}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Customer: {paymentPlan.customer_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Total Amount: {formatCurrency(paymentPlan.total_amount)}
                </Typography>
                <Typography variant="body2">
                  Remaining Balance: {formatCurrency(calculateRemainingBalance())}
                </Typography>
              </Box>
              {calculateRemainingBalance() > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main', alpha: 0.1 }}>
                  <Typography variant="body2" color="info.dark">
                    Note: This payment plan still has a remaining balance. 
                    Marking it as complete will close the plan regardless of outstanding payments.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCompletePlan}
            variant="contained"
            color="success"
          >
            Mark Complete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Payments; 